import { PencilIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';
import Confirm from '@/components/UI/Modal/Confirm';
import { EditableInput } from '@/features/ApplicationForm/Components/ApplicationFormDetails/EditableInput';
import { usePermission } from '@/hooks/usePermission';
import type { ReactSelectOption } from '@/types/Constants';
import { EPermission } from '@/types/Permission';

interface IEditableItemProps {
  id?: string;
  name: string;
  label: string;
  value: any;
  defaultValue?: string | number | ReactSelectOption | undefined;
  inputType?: 'text' | 'textarea' | 'select' | 'file' | 'multiselect';
  options?: ReactSelectOption[];
  validationSchema?: any;
}

const EditableItem = ({ id, label, name, value, defaultValue, inputType, options, validationSchema }: IEditableItemProps) => {
  const [isEditing, setIsEditing] = useState<{ [key: string]: boolean }>({
    name: false,
    confirmationMessage: false,
    emailSubject: false,
    emailMessage: false,
    description: false,
    templateCode: false,
  });
  const [showWarning, setShowWarning] = useState(false);
  const { hasPermission } = usePermission();

  const confirmFormCodeChangeHandler = () => {
    setShowWarning(false);
    setIsEditing((prevState: any) => {
      return {
        ...prevState,
        templateCode: !prevState.templateCode,
      };
    });
  };

  const cancelFormCodeChangeHandler = () => {
    setShowWarning(false);
  };

  const toggleEdit = (key: string, action?: 'save' | 'cancel' | 'edit') => {
    if (key === 'templateCode' && action === 'edit') {
      setShowWarning(true);
      return;
    }
    setIsEditing((prevState: any) => {
      return {
        ...prevState,
        [key]: !prevState[key],
      };
    });
  };

  return (
    <>
      <dt className='text-sm font-medium text-gray-500'>{label}</dt>
      {!isEditing[name] ? (
        <dd className='mt-1 text-sm text-gray-900 flex items-start gap-2'>
          {inputType !== 'file' && <span>{value ? value : 'None'}</span>}
          {hasPermission([EPermission.UPDATE_APPLICATION_FORM]) && (
            <span
              className='text-neutralColor-light hover:text-neutralColor-normal cursor-pointer'
              onClick={() => toggleEdit(name, 'edit')}
            >
              <PencilIcon className='h-5 w-5' />
            </span>
          )}
        </dd>
      ) : (
        <EditableInput
          id={id}
          name={name}
          defaultValue={defaultValue}
          value={value}
          validationSchema={validationSchema}
          toggleEdit={toggleEdit}
          inputType={inputType}
          options={options}
        />
      )}
      {showWarning && (
        <Confirm
          title='Warning'
          message='You are about to edit the application form code. Incorrect form code can make this application form inaccessible. Are you sure you want to update it ?'
          acceptButtonLabel='Yes'
          declineButtonLabel='No'
          accept={confirmFormCodeChangeHandler}
          decline={cancelFormCodeChangeHandler}
        />
      )}
    </>
  );
};

export default EditableItem;
